/**
 * EcosystemMembersModal: platform-admin member-management dialog
 * (P7 wave 3b).
 *
 * Opens from a "Members" row action in `EcosystemManager` and lists the named
 * tenant ecosystem's `EcosystemMembership` records joined to `User` identity.
 * For each member the platform admin can set the per-ecosystem role to
 * `admin` (ecosystem-administrator) or `member` (normal participant); the
 * role-toggle calls `PUT /api/ecosystems/:slug/members/:userId/role`.
 *
 * `status` is shown read-only: membership lifecycle (active / pending /
 * removed) is changed through the join / leave flows, not this surface, so
 * the role control here intentionally touches role only.
 *
 * States handled: loading spinner, load-error alert with retry, empty state
 * (no members), per-row pending-save spinner, and a banner for success /
 * failure of the most recent role change.
 *
 * The modal owns its own data: it loads members on `show`, refetches on
 * retry, and updates the in-memory row optimistically from the server's
 * response so the caller (`EcosystemManager`) does not need to re-fetch the
 * outer table when a role changes.
 *
 * No inline CSS: Bootstrap utilities / PlatformAdmin.css only.
 */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Badge,
    Button,
    Form,
    Modal,
    Spinner,
    Table,
} from 'react-bootstrap';

import { useEcosystemApi } from '@/shared/services/ecosystemApi';

/**
 * Compose a display name from `User.name` ({ first, last }). `User.name` is
 * a structured object in this repo, so rendering it directly throws "Objects
 * are not valid as a React child". Returns an empty string if both parts are
 * missing: callers `|| fallback` it.
 */
function formatName(name) {
    if (!name || typeof name !== 'object') return '';
    const first = (name.first || '').trim();
    const last = (name.last || '').trim();
    return [first, last].filter(Boolean).join(' ');
}

// Bootstrap badge variant per membership status. Mirrors the table-level
// status badge variants used in `EcosystemManager` so the two surfaces read
// consistently. EcosystemMembership.status enum is active | pending | removed.
const STATUS_VARIANT = {
    active: 'success',
    pending: 'warning',
    removed: 'secondary',
};

/**
 * Read-only badge for the membership status column.
 *
 * @param {{status: string}} props
 */
function MembershipStatusBadge({ status }) {
    const variant = STATUS_VARIANT[status] || 'light';
    return (
        <Badge bg={variant} text={variant === 'warning' ? 'dark' : undefined}>
            {status}
        </Badge>
    );
}

MembershipStatusBadge.propTypes = {
    status: PropTypes.string.isRequired,
};

export default function EcosystemMembersModal({ show, ecosystem, onHide }) {
    const ecosystemApi = useEcosystemApi();

    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState('');
    // Per-action banner: { variant: 'success' | 'danger', text }.
    const [banner, setBanner] = useState(null);
    // The userId currently being saved, or null. Used to disable the matching
    // row's role control while its PUT is in flight.
    const [pendingUserId, setPendingUserId] = useState(null);

    const loadMembers = useCallback(async () => {
        if (!ecosystem) {
            return;
        }
        setLoading(true);
        setLoadError('');
        try {
            const data = await ecosystemApi.fetchEcosystemMembers(
                ecosystem.slug
            );
            setMembers(Array.isArray(data) ? data : []);
        } catch {
            setLoadError('Failed to load members. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [ecosystem, ecosystemApi]);

    // Reload members each time the modal opens against a (possibly different)
    // ecosystem. Reset banner / error so they do not bleed across opens.
    useEffect(() => {
        if (!show || !ecosystem) {
            return;
        }
        setBanner(null);
        setLoadError('');
        setMembers([]);
        loadMembers();
    }, [show, ecosystem, loadMembers]);

    // Reset transient UI state when the modal fully closes.
    const handleHide = () => {
        if (pendingUserId) {
            // A role save is in flight: do not allow the modal to close
            // halfway through a write.
            return;
        }
        setBanner(null);
        setLoadError('');
        setMembers([]);
        onHide();
    };

    const handleRoleChange = async (member, nextRole) => {
        if (!ecosystem) {
            return;
        }
        if (nextRole === member.role) {
            return;
        }
        setPendingUserId(member.userId);
        setBanner(null);
        try {
            const updated = await ecosystemApi.updateEcosystemMemberRole(
                ecosystem.slug,
                member.userId,
                nextRole
            );
            // Optimistic-with-server-truth: replace the row with the server's
            // returned shape so any normalisation (trim, default fields) is
            // visible immediately.
            setMembers((prev) =>
                prev.map((row) =>
                    row.userId === member.userId
                        ? {
                              ...row,
                              role: updated?.role ?? nextRole,
                              status: updated?.status ?? row.status,
                          }
                        : row
                )
            );
            setBanner({
                variant: 'success',
                text: `Role updated for ${formatName(member.name) || member.email || member.userId}.`,
            });
        } catch (err) {
            const message =
                err?.response?.data?.message ||
                'Failed to update the member role. Please try again.';
            setBanner({ variant: 'danger', text: message });
        } finally {
            setPendingUserId(null);
        }
    };

    return (
        <Modal show={show} onHide={handleHide} size="lg" scrollable>
            <Modal.Header closeButton={!pendingUserId}>
                <Modal.Title>
                    {ecosystem
                        ? `Members of ${ecosystem.name}`
                        : 'Ecosystem members'}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {banner && (
                    <Alert
                        variant={banner.variant}
                        dismissible
                        onClose={() => setBanner(null)}
                    >
                        {banner.text}
                    </Alert>
                )}

                {loadError && (
                    <Alert variant="danger">
                        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                            <span>{loadError}</span>
                            <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={loadMembers}
                            >
                                Retry
                            </Button>
                        </div>
                    </Alert>
                )}

                {loading ? (
                    <div className="text-center py-5">
                        <Spinner
                            animation="border"
                            variant="primary"
                            role="status"
                        >
                            <span className="visually-hidden">Loading…</span>
                        </Spinner>
                    </div>
                ) : !loadError && members.length === 0 ? (
                    <p className="text-muted text-center my-4 mb-0">
                        No members yet for this ecosystem.
                    </p>
                ) : !loadError ? (
                    <Table responsive hover className="mb-0 align-middle">
                        <thead>
                            <tr>
                                <th>Member</th>
                                <th>Status</th>
                                <th>Joined</th>
                                <th>Role</th>
                            </tr>
                        </thead>
                        <tbody>
                            {members.map((member) => {
                                const isPending =
                                    pendingUserId === member.userId;
                                return (
                                    <tr key={member.userId}>
                                        <td>
                                            <div className="fw-semibold">
                                                {formatName(member.name) ||
                                                    member.email ||
                                                    'Unknown member'}
                                            </div>
                                            {member.email && (
                                                <div className="small text-muted">
                                                    {member.email}
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            <MembershipStatusBadge
                                                status={member.status}
                                            />
                                        </td>
                                        <td className="small text-muted">
                                            {member.joinedAt
                                                ? new Date(
                                                      member.joinedAt
                                                  ).toLocaleDateString()
                                                : 'N/A'}
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center gap-2">
                                                <Form.Select
                                                    size="sm"
                                                    aria-label={`Role for ${
                                                        formatName(
                                                            member.name
                                                        ) ||
                                                        member.email ||
                                                        member.userId
                                                    }`}
                                                    value={member.role}
                                                    disabled={isPending}
                                                    onChange={(e) =>
                                                        handleRoleChange(
                                                            member,
                                                            e.target.value
                                                        )
                                                    }
                                                    className="platform-admin-role-select"
                                                >
                                                    <option value="member">
                                                        Member
                                                    </option>
                                                    <option value="admin">
                                                        Admin
                                                    </option>
                                                </Form.Select>
                                                {isPending && (
                                                    <Spinner
                                                        animation="border"
                                                        size="sm"
                                                        role="status"
                                                    >
                                                        <span className="visually-hidden">
                                                            Saving…
                                                        </span>
                                                    </Spinner>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                ) : null}
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant="secondary"
                    onClick={handleHide}
                    disabled={Boolean(pendingUserId)}
                >
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

EcosystemMembersModal.propTypes = {
    // Whether the modal is open.
    show: PropTypes.bool.isRequired,
    // The ecosystem whose members are being managed. Required when show=true;
    // ignored otherwise. Passed by EcosystemManager from its table row.
    ecosystem: PropTypes.shape({
        _id: PropTypes.string,
        slug: PropTypes.string.isRequired,
        name: PropTypes.string,
    }),
    // Called when the modal requests close (X button or "Close" button).
    onHide: PropTypes.func.isRequired,
};

EcosystemMembersModal.defaultProps = {
    ecosystem: null,
};
